import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { isAppReloadHeld, isAppReloadSafePath, reloadToVersion } from "@/lib/app-reload-guard";

const VERSION_FILE_URL = "/version.json";
const MIN_VERSION_CHECK_INTERVAL_MS = 30_000;

interface VersionFile {
  version: string;
}

function isVersionFile(value: unknown): value is VersionFile {
  return (
    typeof value === "object" &&
    value !== null &&
    "version" in value &&
    typeof value.version === "string" &&
    value.version.length > 0
  );
}

async function fetchLatestVersion() {
  const response = await fetch(`${VERSION_FILE_URL}?t=${Date.now()}`, {
    cache: "no-store",
  });

  if (!response.ok) return null;

  const payload: unknown = await response.json();

  if (!isVersionFile(payload)) return null;

  return payload.version;
}

/**
 * Reloads onto a newly deployed bundle, but only at a safe moment: never while
 * a checkout holds the reload guard and never on the checkout routes. A version
 * seen at an unsafe moment stays pending and is applied on the next route
 * change or the next check. Incident 2026-09-16: a deploy-triggered reload
 * aborted a customer's checkout POST mid-flight ("Sem conexão com a loja").
 */
export function useAppVersionRefresh() {
  const { pathname } = useLocation();
  const pendingVersionRef = useRef<string | null>(null);
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  useEffect(() => {
    let checking = false;
    let lastCheckedAt = 0;

    function applyIfSafe() {
      const version = pendingVersionRef.current;
      if (!version) return;
      if (isAppReloadHeld() || !isAppReloadSafePath(pathnameRef.current)) return;
      pendingVersionRef.current = null;
      reloadToVersion(version);
    }

    async function checkVersion(force = false) {
      const now = Date.now();

      if (checking) return;
      if (!force && now - lastCheckedAt < MIN_VERSION_CHECK_INTERVAL_MS) {
        applyIfSafe();
        return;
      }

      checking = true;
      lastCheckedAt = now;

      try {
        const latestVersion = await fetchLatestVersion();
        if (latestVersion && latestVersion !== __APP_VERSION__) {
          pendingVersionRef.current = latestVersion;
        }
        applyIfSafe();
      } catch {
        return;
      } finally {
        checking = false;
      }
    }

    function handleFocus() {
      void checkVersion();
    }

    function handleVisibilityChange() {
      if (document.visibilityState === "visible") void checkVersion();
    }

    function handlePageShow(event: PageTransitionEvent) {
      void checkVersion(event.persisted);
    }

    void checkVersion(true);

    window.addEventListener("focus", handleFocus);
    window.addEventListener("pageshow", handlePageShow);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("pageshow", handlePageShow);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const version = pendingVersionRef.current;
    if (!version) return;
    if (isAppReloadHeld() || !isAppReloadSafePath(pathname)) return;
    pendingVersionRef.current = null;
    reloadToVersion(version);
  }, [pathname]);
}
